import { FIELD_LABELS, emptyAnswers } from "@/constants/preformatifDiagnostic";
import { PreformatifDiagnosticAnswers } from "@/interfaces/PreformatifDiagnostic";
import {
  useFinalizePreformatifDiagnosticMutation,
  useGetPreformatifDiagnosticHeaderQuery,
  useGetPreformatifDiagnosticQuery,
  useLazyExportPreformatifDiagnosticPdfQuery,
  useSavePreformatifDiagnosticMutation,
  useUpdatePreformatifDiagnosticMutation,
} from "@/services/companies/courses/preformatifDiagnostic";
import { useEffect, useMemo, useState } from "react";

export interface PreformatifDiagnosticFeedback {
  variant: "success" | "danger";
  message: string;
}

/** Comparaison insensible à l'ordre des clés des grilles (modules, moyens). */
const stable = (value: unknown) =>
  JSON.stringify(value, (_key, item) =>
    item && typeof item === "object" && !Array.isArray(item)
      ? Object.fromEntries(
          Object.entries(item).sort(([a], [b]) => a.localeCompare(b)),
        )
      : item,
  );

const errorMessage = (error: unknown, fallback: string) =>
  (error as { data?: { message?: string } })?.data?.message || fallback;

/**
 * État et actions d'un diagnostic préformatif : brouillon, finalisation,
 * export PDF. Partagé par la modale et la page du dossier de formation.
 */
const usePreformatifDiagnosticEditor = (
  companyId: string,
  courseId?: string,
) => {
  const skip = !courseId;
  const args: [string, string] = [companyId, courseId || ""];
  const {
    data: diagnostic,
    isLoading,
    isError,
  } = useGetPreformatifDiagnosticQuery(args, { skip });
  const { data: header } = useGetPreformatifDiagnosticHeaderQuery(args, {
    skip,
  });
  const [save, saveState] = useSavePreformatifDiagnosticMutation();
  const [update, updateState] = useUpdatePreformatifDiagnosticMutation();
  const [finalizeDiagnostic, finalizeState] =
    useFinalizePreformatifDiagnosticMutation();
  const [exportPdf, exportState] = useLazyExportPreformatifDiagnosticPdfQuery();

  const saved = useMemo<PreformatifDiagnosticAnswers>(
    () => ({ ...emptyAnswers(), ...diagnostic?.answers }),
    [diagnostic],
  );
  const [answers, setAnswers] = useState(saved);
  const [missing, setMissing] = useState<string[]>([]);
  const [feedback, setFeedback] =
    useState<PreformatifDiagnosticFeedback | null>(null);

  useEffect(() => setAnswers(saved), [saved]);
  useEffect(() => {
    setMissing([]);
    setFeedback(null);
  }, [courseId]);

  const reset = () => {
    setMissing([]);
    setFeedback(null);
  };

  const saveDraft = async () => {
    if (!courseId) return;
    reset();
    const result = diagnostic
      ? await update([companyId, courseId, answers])
      : await save([companyId, courseId, answers]);
    setFeedback(
      "error" in result
        ? {
            variant: "danger",
            message: errorMessage(
              result.error,
              "Impossible d’enregistrer le diagnostic.",
            ),
          }
        : { variant: "success", message: "Brouillon enregistré." },
    );
  };

  const finalize = async () => {
    if (!courseId) return;
    reset();
    if (!diagnostic) {
      const created = await save([companyId, courseId, answers]);
      if ("error" in created) {
        setFeedback({
          variant: "danger",
          message: errorMessage(
            created.error,
            "Impossible d’enregistrer le diagnostic.",
          ),
        });
        return;
      }
    }
    const result = await finalizeDiagnostic([companyId, courseId, answers]);
    if (!("error" in result)) {
      setFeedback({ variant: "success", message: "Diagnostic finalisé." });
      return;
    }
    const fields: string[] =
      (result.error as { data?: { fields?: string[] } })?.data?.fields || [];
    setMissing(fields);
    setFeedback({
      variant: "danger",
      message: fields.length
        ? `À compléter avant de finaliser : ${fields
            .map(
              (field) =>
                FIELD_LABELS[field as keyof PreformatifDiagnosticAnswers] ||
                field,
            )
            .join(", ")}.`
        : errorMessage(result.error, "Impossible de finaliser le diagnostic."),
    });
  };

  /** Exporte la version enregistrée : vierge si le diagnostic n'a pas été commencé. */
  const downloadPdf = async () => {
    if (!courseId) return;
    reset();
    const result = await exportPdf([companyId, courseId]);
    if (result.data?.link) window.open(result.data.link, "_blank");
    else
      setFeedback({
        variant: "danger",
        message: errorMessage(result.error, "Impossible de générer le PDF."),
      });
  };

  return {
    diagnostic,
    header,
    answers,
    setAnswers,
    missing,
    feedback,
    isLoading,
    isError,
    dirty: stable(answers) !== stable(saved),
    busy:
      saveState.isLoading || updateState.isLoading || finalizeState.isLoading,
    exporting: exportState.isFetching,
    saveDraft,
    finalize,
    downloadPdf,
  };
};

export default usePreformatifDiagnosticEditor;
